// A refused save in plain words (WEB-DSH-MAN-03): each DSH-V violation by its rule, the free plan's refusal, or the
// API's own message.
import { ApiError } from "../../api/problem";

type Violation = { check: string; widget?: number | null; message: string };
type T = (key: string, options?: Record<string, unknown>) => string;

const CHECKS = new Set(["DSH-V-01", "DSH-V-02", "DSH-V-03", "DSH-V-04", "schema", "unknown_image"]);

export function saveErrors(error: unknown, t: T, widgetName: (index: number) => string | undefined): string[] {
  if (!(error instanceof ApiError)) return [t("dashboards.error.unknown")];
  const violations = (error.problem as { violations?: Violation[] } | undefined)?.violations ?? [];
  if (error.status === 400 && violations.length) {
    return violations.map((v) => {
      const text = CHECKS.has(v.check) ? t(`dashboards.violation.${v.check}`) : v.message;
      const name = typeof v.widget === "number" ? widgetName(v.widget) : undefined;
      return name ? `${name}: ${text}` : text;
    });
  }
  if (error.status === 403) return [t("dashboards.error.forbidden"), error.message];
  if (isSaveConflict(error)) return [t("dashboards.error.changed")];
  return [error.message];
}

/**
 * A widget's name in an error line: its title, else its first value's name, else its number, by its index in the
 * document that was sent, not the one saved before (#9).
 */
export function widgetLabel(
  sent: { widgets: { title?: string; bindings?: { device: string; datapoint?: string | null }[] }[] } | undefined,
  points: Record<string, { name?: string }> | undefined,
  i: number,
): string | undefined {
  const w = sent?.widgets[i];
  if (!w) return undefined;
  const b = w.bindings?.find((x) => x.datapoint);
  return w.title ?? (b ? points?.[`${b.device}/${b.datapoint}`]?.name : undefined) ?? `#${i + 1}`;
}

/** The widgets a refused save names, by their index in the document sent, to mark them on the grid (#9). */
export function refusedWidgets(error: unknown): number[] {
  if (!(error instanceof ApiError) || error.status !== 400) return [];
  const violations = (error.problem as { violations?: Violation[] } | undefined)?.violations ?? [];
  return violations.flatMap((v) => (typeof v.widget === "number" ? [v.widget] : []));
}

/** The dashboard was saved somewhere else since it was loaded (dashboard-save-conflict DSC-03, DSC-06). */
export function isSaveConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 412;
}
